import { cva, type VariantProps } from 'class-variance-authority'
import { type ButtonHTMLAttributes, type HTMLAttributes } from 'react'

import { cn } from '../cn'

// The chip family (design/components/chip.html). Four related but distinct
// signalling atoms. Rule across all: never communicate by colour alone — every
// one carries text; colour reinforces.

/* ── Chip — status in a pill ─────────────────────────────────────────── */

const chipVariants = cva('inline-flex items-center gap-1 rounded-xs font-bold tracking-[0.03em]', {
  variants: {
    tone: {
      ok: 'bg-ok-bg text-ok',
      warn: 'bg-warn-bg text-warn',
      sens: 'bg-sens-bg text-sens',
      info: 'bg-info-bg text-info',
      draft: 'bg-field text-text-2',
    },
    size: {
      sm: 'px-[7px] py-[2px] text-[10px] [&_svg]:size-[11px]',
      md: 'rounded-[6px] px-[10px] py-[3px] text-[11.5px] [&_svg]:size-3',
    },
  },
  defaultVariants: { tone: 'draft', size: 'sm' },
})

export interface ChipProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof chipVariants> {
  /** Leading dot in the chip's own colour. */
  dot?: boolean
}

export function Chip({ className, tone, size, dot, children, ...rest }: ChipProps) {
  return (
    <span className={cn(chipVariants({ tone, size }), className)} {...rest}>
      {dot && <span aria-hidden className="size-[5px] rounded-full bg-current" />}
      {children}
    </span>
  )
}

/* ── StatusBadge — dot + label (no pill) ─────────────────────────────── */

const statusBadgeTones = {
  ok: { text: 'text-ok', dot: 'bg-ok' },
  warn: { text: 'text-warn', dot: 'bg-warn' },
  sens: { text: 'text-sens', dot: 'bg-sens' },
  off: { text: 'text-text-3', dot: 'bg-text-3' },
} as const

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  status: keyof typeof statusBadgeTones
}

/**
 * Coloured dot + text label without a pill background, for inline status
 * (ok/warn/sens/off). Text always accompanies the dot — never colour alone.
 */
export function StatusBadge({ status, className, children, ...rest }: StatusBadgeProps) {
  const t = statusBadgeTones[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-[11px] font-semibold',
        t.text,
        className,
      )}
      {...rest}
    >
      <span aria-hidden className={cn('size-1.5 rounded-full', t.dot)} />
      {children}
    </span>
  )
}

/* ── TagChip — descriptive label, optionally removable ───────────────── */

const tagChipTones = {
  prio: 'bg-warn-bg text-warn',
  sens: 'bg-sens-bg text-sens',
  gray: 'bg-field text-text-2',
} as const

export interface TagChipProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: keyof typeof tagChipTones
  onRemove?: () => void
  /** Accessible label for the × button when removable. */
  removeLabel?: string
}

export function TagChip({
  tone = 'gray',
  onRemove,
  removeLabel,
  className,
  children,
  ...rest
}: TagChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2 py-[3px] text-[10.5px] font-bold tracking-[0.02em]',
        tagChipTones[tone],
        className,
      )}
      {...rest}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ?? 'Quitar etiqueta'}
          className="inline-flex opacity-60 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            className="size-[10px]"
            aria-hidden
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  )
}

/* ── FilterChip — selectable filter (a real control) ─────────────────── */

export interface FilterChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-pressed'> {
  pressed: boolean
  count?: number
}

export function FilterChip({
  pressed,
  count,
  className,
  children,
  type = 'button',
  ...rest
}: FilterChipProps) {
  return (
    <button
      type={type}
      aria-pressed={pressed}
      className={cn(
        'inline-flex cursor-pointer items-center gap-1.5 rounded-pill border border-transparent px-3 py-1.5 text-[11.5px] font-semibold',
        'transition-colors duration-quick ease-vysto',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue focus-visible:outline-offset-2',
        pressed
          ? 'bg-brand-blue text-white'
          : 'bg-field text-text-2 hover:bg-hover hover:text-text',
        className,
      )}
      {...rest}
    >
      {children}
      {count != null && <span className="font-bold tabular-nums opacity-75">{count}</span>}
    </button>
  )
}
